<template>
	<el-dialog
		:modelValue="open"
		@update:modelValue="$emit('update:open', $event)"
		:title="'Vorzeitige Entscheidung'"
		@close="handleClose"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			:waitingMsg="texts.formErrors.waiting"
			:showErr="showErr"
			:errMsg="errMsg"
			ref="formRef"
			:id="id"
		>
			<el-form-item prop="state">
				<el-select v-model="request.state" placeholder="Bitte wählen">
					<el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item" />
				</el-select>
			</el-form-item>

			<el-form-item label="Begründung:" prop="text">
				<el-input v-model="request.text" :autosize="{ minRows: 10 }" type="textarea" placeholder="Bitte Begründung hier eingeben." />
			</el-form-item>
		</TheForm>
		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="save" :disabled="waiting" :loading="waiting">Veröffentlichen</TheButton>
				<TheButton type="default" @buttonclick="handleClose">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import { texts } from '../../utils/texts';
import { validateForm, resetForm, dialogBeforeClose } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { casePreliminaryDecision } from '../../requests/cases';
import { PreliminaryDecisionRequest, CaseState } from '../../types/types';
import { returnFormOptions } from '@/utils/formOptions.js';
import { prepareSearchRequest } from '@/utils/search/searchUtils';

interface PreliminaryDecisionData {
	request: {
		state: CaseState | null;
		text: string;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	showErr: boolean;
	errMsg: string;
}

export default defineComponent({
	name: 'DialogPreliminaryDecision',
	data(): PreliminaryDecisionData {
		return {
			request: {
				state: null,
				text: '',
			},
			rules: {
				state: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			id: 'preliminaryDecisionId',
			waiting: false,
			showErr: false,
			errMsg: texts.cases.preliminaryDecisionFailed,
		};
	},
	props: {
		open: {
			type: Boolean,
			required: true,
		},
		caseId: {
			type: String,
			required: true,
		},
	},
	emits: ['reload', 'update:open'],
	components: {
		TheButton,
		TheForm,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		statusOptions() {
			return returnFormOptions('caseStateDecisionsOptions', false, false);
		},
		texts() {
			return texts;
		},
	},

	methods: {
		async save() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.waiting = true;

			let searchRequest = prepareSearchRequest(this.request);

			const requestPreliminaryDecision = {
				caseId: String(this.caseId),
				...searchRequest,
			} as PreliminaryDecisionRequest;

			const result = await casePreliminaryDecision(requestPreliminaryDecision);
			if (result.success) {
				this.handleClose(true);
			} else {
				this.showErr = true;
			}

			this.waiting = false;
		},
		handleClose(reload: Boolean) {
			resetForm(this.formRef, this.id);

			this.showErr = false;

			if (reload) {
				this.$emit('reload');
			}
			this.$emit('update:open', false);
		},
		dialogBeforeClose,
	},
});
</script>

<style lang="scss"></style>
